// The CIS deduction calculator on /tools/cis-calculator/.
//
// Same shape as interest-calculator.js, and for the same reason: it is a thin
// DOM layer over shared/money.js — the module the SERVER settles invoices with
// — bundled in by scripts/build-web.mjs. The page cannot quote a deduction the
// product wouldn't apply, because it is running the product's arithmetic.
//
// The whole point of the tool is the thing most people get wrong and every
// competitor makes you configure by hand: the deduction comes off LABOUR only.
// Materials are never deducted. Getting that backwards is what makes a
// subcontractor's invoice bounce back from the main contractor.
import { computeCisDeductionPence } from "../../shared/money.js";
import { readMoneyText } from "../../shared/money-input.js";

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });

// "1,240.50" / "£1,240.50" / " 1240.5 " -> 124050, by the product's own rule
// (shared/money-input.js). Anything that isn't an amount is 0, so an empty or
// half-typed field shows zeros rather than NaN, and "1.240,50" is never £1.24.
function toPence(raw) {
  const { pence } = readMoneyText(raw);
  return pence != null && pence > 0 ? pence : 0;
}

function init() {
  const form = document.getElementById("cis-calc");
  if (!form) return;

  const labourEl = document.getElementById("calc-labour");
  const materialsEl = document.getElementById("calc-materials");
  const rateEl = document.getElementById("calc-rate");
  const out = {
    labour: document.getElementById("out-labour"),
    materials: document.getElementById("out-materials"),
    gross: document.getElementById("out-gross"),
    deduction: document.getElementById("out-deduction"),
    net: document.getElementById("out-net"),
    rateNote: document.getElementById("out-rate-note")
  };

  function recalc() {
    const labourPence = toPence(labourEl.value);
    const materialsPence = toPence(materialsEl.value);
    const rate = Number.parseFloat(String(rateEl.value || "0"));

    // The product's own function. Rate applies to labour, clamped to labour.
    const deductionPence = computeCisDeductionPence({ rate, labourPence });
    const grossPence = labourPence + materialsPence;
    const netPence = grossPence - deductionPence;

    out.labour.textContent = gbp.format(labourPence / 100);
    out.materials.textContent = gbp.format(materialsPence / 100);
    out.gross.textContent = gbp.format(grossPence / 100);
    out.deduction.textContent = "− " + gbp.format(deductionPence / 100);
    out.net.textContent = gbp.format(netPence / 100);

    // Spell out what was and wasn't deducted from — the single most common
    // mistake this tool exists to prevent.
    out.rateNote.textContent = materialsPence > 0
      ? `${rate}% of the ${gbp.format(labourPence / 100)} labour. The ${gbp.format(materialsPence / 100)} of materials is not deducted from.`
      : `${rate}% of the ${gbp.format(labourPence / 100)} labour.`;
  }

  form.addEventListener("input", recalc);
  form.addEventListener("change", recalc);
  // Never submit: there is no server side to this, and a reload would wipe the
  // figures the visitor just typed.
  form.addEventListener("submit", (event) => event.preventDefault());
  recalc();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
